// ABOUTME: Export panel for the bulk import/export modal — pick entity groups and download one bundle.
import {Box, Button, Checkbox, FormControlLabel, FormGroup, Typography} from '@mui/material'
import React, {useMemo, useState} from 'react'
import {useSnackbar} from 'notistack'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useBulkExport} from '../hooks/useConfigImportExport'
import {Entity, EntityType, getEntityApiPath} from '../types'

interface BulkExportPanelProps {
  entities: Entity[]
  supportedKeys: string[]
  countryId: string
  businessLine: string
  onClose: () => void
}

export const BulkExportPanel: React.FC<BulkExportPanelProps> = ({
  entities,
  supportedKeys,
  countryId,
  businessLine,
  onClose
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const bulkExport = useBulkExport()

  const options = useMemo(
    () =>
      entities
        .map((entity) => ({key: getEntityApiPath(entity.id as EntityType), name: entity.name}))
        .filter((option) => supportedKeys.includes(option.key)),
    [entities, supportedKeys]
  )

  const [selected, setSelected] = useState<string[]>([])
  const allSelected = options.length > 0 && selected.length === options.length

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  const toggleAll = () => setSelected(allSelected ? [] : options.map((option) => option.key))

  const handleExport = async () => {
    try {
      await bulkExport.mutateAsync({entityKeys: selected, countryId, businessLine})
      onClose()
    } catch {
      enqueueSnackbar(t('countryConfigurations.importExport.exportError'), {
        variant: 'error',
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      })
    }
  }

  if (options.length === 0) {
    return <Typography variant="body2">{t('countryConfigurations.importExport.bulk.noEntities')}</Typography>
  }

  return (
    <>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              onChange={toggleAll}
            />
          }
          label={t('countryConfigurations.importExport.bulk.selectAll')}
        />
        {options.map((option) => (
          <FormControlLabel
            key={option.key}
            control={<Checkbox checked={selected.includes(option.key)} onChange={() => toggle(option.key)} />}
            label={option.name}
          />
        ))}
      </FormGroup>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
        <Button
          variant="contained"
          disabled={selected.length === 0 || bulkExport.isPending}
          onClick={handleExport}
        >
          {t('countryConfigurations.importExport.bulk.exportSelected')}
        </Button>
      </Box>
    </>
  )
}
